import dayjs from 'dayjs';

import { getDateString, getFormattedDate } from 'helpers/dates';

import {
  MYSTERY_SHOP_MODAL_REQUEST_FROM,
  SHOP_REQUEST_COLUMNS,
  SHOP_REQUEST_STATUS,
  SHOP_REQUEST_TYPE,
} from './constants';
import {
  MysteryShopRequestGroup,
  MysteryShopRequestInbound,
  MysteryShopRequestOutbound,
  MysteryShopRequestsBatch,
  ShopRequestCreationObject,
  ShopRequestPropagation,
  ShopRequestsFiltersType,
  ShopRequestTranscriptRequirementType,
} from './types';
import { CommunityType } from '../../types/communities';

export const createShopRequestOutbound = (
  requestGroupName: string,
  base_community_id: number | undefined,
  company_id: number | undefined,
  repeat: number,
  endRepeat: number,
  shopRequests: ShopRequestCreationObject[]
): MysteryShopRequestGroup => ({
  name: requestGroupName,
  base_community_id,
  company_id,
  repeat,
  end_repeat: endRepeat,
  shop_requests: shopRequests.map((shopRequest) => ({
    community_id: shopRequest.communityId,
    independent_living: shopRequest.independentLiving,
    assisted_living: shopRequest.assistedLiving,
    memory_care: shopRequest.memoryCare,
    admin_note: shopRequest.adminNote,
    due_date: shopRequest.dueDate,
    shopper_id: shopRequest.shopperId,
    auditor_id: shopRequest.auditorId,
    shopper_note: shopRequest.shopperNote,
    status: shopRequest.status,
    request_type: shopRequest.requestType,
    community_has_web_form: false,
    shopper_contact_connected: false,
    website_review: false,
    fee_information: false,
    apartment_starting_rates: false,
    basic_floor_plan_rates: false,
    floor_plans_sq_footage: false,
    care_pricing_type: false,
    care_pricing: false,
    specials_incentives: false,
    typical_rate_increase_annaully: false,
    email_correspondence_application_attachments: false,
    respite_fee_by_care_type: false,
    rate_documentation: false,
    occupancy_data_per_care_level: false,
    sample_menu_activity_calendar: false,
    company_id,
    propagation: shopRequest.propagation,
  })),
});

export const transformShopRequestOutbound = (shopRequest: MysteryShopRequestInbound): MysteryShopRequestOutbound => ({
  community_id: shopRequest.community.id,
  independent_living: shopRequest.independentLiving,
  assisted_living: shopRequest.assistedLiving,
  memory_care: shopRequest.memoryCare,
  admin_note: shopRequest.adminNote,
  due_date: shopRequest.dueDate,
  repeat: shopRequest.repeat,
  end_repeat: shopRequest.endRepeat,
  shopper_id: shopRequest.shopper?.userId || null,
  auditor_id: shopRequest.auditor?.userId || null,
  shopper_note: shopRequest.shopperNote,
  status: shopRequest.status,
  request_type: shopRequest.request_type,
  community_has_web_form: shopRequest.community_has_web_form,
  shopper_contact_connected: shopRequest.shopper_contact_connected,
  website_review: shopRequest.website_review,
  fee_information: shopRequest.fee_information,
  apartment_starting_rates: shopRequest.apartment_starting_rates,
  basic_floor_plan_rates: shopRequest.basic_floor_plan_rates,
  floor_plans_sq_footage: shopRequest.floor_plans_sq_footage,
  care_pricing_type: shopRequest.care_pricing_type,
  care_pricing: shopRequest.care_pricing,
  specials_incentives: shopRequest.specials_incentives,
  typical_rate_increase_annaully: shopRequest.typical_rate_increase_annaully,
  email_correspondence_application_attachments: shopRequest.email_correspondence_application_attachments,
  respite_fee_by_care_type: shopRequest.respite_fee_by_care_type,
  rate_documentation: shopRequest.rate_documentation,
  occupancy_data_per_care_level: shopRequest.occupancy_data_per_care_level,
  sample_menu_activity_calendar: shopRequest.sample_menu_activity_calendar,
  propagation: shopRequest.propagation,
});

export const getShopRequestName = (name: string | null, requestFrom: number, selectedBaseCommunity: any): string => {
  if (name && name?.length) {
    return name;
  } else if (requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.COMPANY && selectedBaseCommunity?.comunity_name) {
    return `${selectedBaseCommunity.comunity_name}'s Competitors`;
  }
  return `Request from ${getFormattedDate(new Date(), 'MMM D, YYYY')}`;
};

export const getInitials = (name: string): string => {
  const matches = name.match(/(\b\S)/g);
  if (!matches) {
    return '';
  }
  return matches.join('').toUpperCase();
};

export const getShopRequestGroupsWithKeys = (groups: MysteryShopRequestGroup[] | null) => {
  if (!groups) {
    return [];
  }

  return groups.map((group) => ({
    ...group,
    key: getShopRequestGroupKey(group),
    shopRequests: group.shop_requests!.map((request) => ({
      ...request,
      key: getShopRequestKey(request),
    })),
  }));
};

export const getShopRequestKey = (shopRequest: MysteryShopRequestOutbound) => `shopRequest-${shopRequest.id}`;
export const getShopRequestGroupKey = (group: MysteryShopRequestGroup) => `shopRequestGroup-${group.id}`;

export const getTranscriptRequirementActorName = (requirement: ShopRequestTranscriptRequirementType) =>
  requirement.updated_by?.full_name || requirement.updated_by?.email || 'Unknown user';

export const getTranscriptRequirementUpdatedAtText = (requirement: ShopRequestTranscriptRequirementType) => {
  if (!requirement.updated_at) {
    return '';
  }

  return ` on ${getFormattedDate(requirement.updated_at, 'MMMM D, YYYY [at] h:mm A')}`;
};

export const getShopRequestListingPayload = (
  filters: Partial<ShopRequestsFiltersType>,
  ordering: string | undefined
) => {
  const payload: any = {
    search: filters.search,
    page: filters.page,
    page_size: filters.pageSize,
    status: filters.status,
    shoppers: filters.shoppers,
    auditors: filters.auditors,
    due_date_start: filters.dueDateStart ? dayjs(filters.dueDateStart).format('YYYY-MM-DD') : undefined,
    due_date_end: filters.dueDateEnd ? dayjs(filters.dueDateEnd).format('YYYY-MM-DD') : undefined,
    due_date_range: filters.dueDateRange,
    request_type: filters.requestType?.length ? filters.requestType : undefined,
    company_ids: filters.companyIds,
    no_propagation: filters.noPropagation || undefined,
    group_by: filters.groupBy || undefined,
    order_by: ordering,
  };

  return payload;
};

export const getShopRequestOrderingFromSorter = (sorter: any, defaultOrdering = SHOP_REQUEST_COLUMNS.dueDate.id) => {
  if (!sorter?.column || !sorter.order) {
    return defaultOrdering;
  }

  return sorter.order === 'descend' ? `-${sorter.column.id}` : sorter.column.id;
};

export const getShopRequestSortOrder = (
  ordering: string | undefined,
  columnId: string
): 'ascend' | 'descend' | null => {
  if (ordering?.replace(/^-/, '') !== columnId) {
    return null;
  }

  return ordering.startsWith('-') ? 'descend' : 'ascend';
};

export const getShopRequestPageFromTableChange = (pagination: any, extra: any) =>
  extra?.action === 'sort' ? 1 : pagination?.current;

export const transformBatchShopRequestOutbound = (
  batchShopRequest: Partial<MysteryShopRequestsBatch>
): Partial<MysteryShopRequestsBatch> => {
  const { shopper_id, auditor_id, ...rest } = batchShopRequest;

  return {
    ...rest,
    ...(shopper_id === -1 ? { shopper_id: null } : shopper_id !== undefined ? { shopper_id } : {}),
    ...(auditor_id === -1 ? { auditor_id: null } : auditor_id !== undefined ? { auditor_id } : {}),
  };
};

type ShopRequestProps = {
  baseCommunityId: number;
  independentLiving: boolean;
  assistedLiving: boolean;
  memoryCare: boolean;
  dueDate: string;
  specificDueDate?: Date;
  repeat: number;
  shopperId: number | null;
  auditorId: number | null;
  requestFrom: number;
  requestType: ShopRequestCreationObject['requestType'];
  selectedCompany: any;
  propagation: ShopRequestPropagation;
};

export const getBaseCommunityShopRequest = ({
  baseCommunityId,
  independentLiving,
  assistedLiving,
  memoryCare,
  dueDate,
  specificDueDate,
  repeat,
  shopperId,
  auditorId,
  requestFrom,
  requestType,
  selectedCompany,
  propagation,
}: ShopRequestProps) => ({
  communityId: baseCommunityId,
  communityName: '',
  independentLiving,
  assistedLiving,
  memoryCare,
  adminNote: null,
  dueDate: getDateString(dueDate, specificDueDate),
  repeat,
  shopperId,
  auditorId,
  requestFrom: requestFrom === MYSTERY_SHOP_MODAL_REQUEST_FROM.COMPANY ? selectedCompany : null,
  requestType,
  status: SHOP_REQUEST_STATUS[0].id,
  propagation,
});

export const getShopRequestPropagationCommunities = (shopRequest: MysteryShopRequestInbound | null) => {
  if (!shopRequest) {
    return [];
  }

  if (shopRequest.request_type !== SHOP_REQUEST_TYPE.MYSTERY_SHOP) {
    return [];
  }

  const communities = [];

  if (shopRequest?.propagation?.propagate_self) {
    communities.push(shopRequest.community);
  }
  if (shopRequest?.propagation?.propagate_others) {
    communities.push(...(shopRequest?.related_communities ?? []));
  }

  return communities;
};
